import React, { useState, useContext, useRef } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import NtText from "../../components/NtText";
import colors from "../../config/colors";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import UserContext from "../../context/UserContext";
import NtModalUnactiveButton from "../../components/NtModalUnactiveButton";
import NtModalActiveButton from "../../components/NtModalActiveButton";
import NtModalInputField from "../../components/NtModalInputField";
import NtToggleRow from "../../components/NtToggleRow";
import Logger from "../../common/utils/Logger";
import { parseError } from "@ntconnect/framework/pbx";
import NtModalAccountPickerField from "./NtModalAccountPickerField";
import NtModalError from "../../components/NtModalError";
import { deleteInboundRoute, updateInboundRoute } from "../../api/inboundroute";
import NtModalTitle from "../../components/NtModalTitle";
import NtModalSubtitle from "../../components/NtModalSubtitle";
import NtModal from "../../components/NtModal";
import NtInboundRouteXmlEditor from "./NtInboundRouteXmlEditor";

// Backend returns `dialplan_enabled` as a boolean-ish string in some places —
// coerce defensively so the toggle reflects the current state correctly.
function asBool(value, fallback = true) {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return fallback;
}

function NtInboundRouteUpdateForm({ item, onCancel, onUpdate, onDelete }) {
  const [name, setName] = useState(item.dialplan_name);
  const [destination, setDestination] = useState(item.dialplan_number);
  const [description, setDescription] = useState(item.dialplan_description || "");
  const [action, setAction] = useState(item.dialplan_destination_actions);
  const [callerIdName, setCallerIdName] = useState(
    item.destination?.destination_caller_id_name || ""
  );
  const [callerIdNumber, setCallerIdNumber] = useState(
    item.destination?.destination_caller_id_number || ""
  );
  const [order, setOrder] = useState(
    item.dialplan_order != null ? String(item.dialplan_order) : ""
  );
  const [enabled, setEnabled] = useState(asBool(item.dialplan_enabled, true));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const context = useContext(UserContext);
  const xmlModalRef = useRef();

  const isRequiredFields = () => {
    return name && destination && action;
  };

  const handleUpdate = async () => {
    setLoading(true);

    setError(null);

    const params = {
      name,
      destination,
      description: description || undefined,
      action,
      enabled,
      order: order ? parseInt(order, 10) : undefined,
      callerIdName,
      callerIdNumber,
    };

    const response = await updateInboundRoute(
      context.user?.domain,
      params,
      item.dialplan_uuid,
    );

    Logger("Inbound Route Update", response);

    if (response.ok) {
      if (onUpdate) {
        onUpdate();
      }
    } else {
      setError(parseError(response));
    }

    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    setError(null);

    const response = await deleteInboundRoute(
      context.user?.domain,
      item?.dialplan_uuid
    );

    if (response.ok) {
      if (onDelete) {
        onDelete();
      }
    } else {
      setError(parseError(response));
    }

    setLoading(false);
  };

  return (
    <View style={styles.container}>
      <NtModalTitle style={styles.title}>Update Inbound Route</NtModalTitle>
      <NtModalSubtitle style={[{ marginTop: 4 }]}>
        Update Inbound Route{" "}
        <NtText style={{ fontWeight: "900" }}>{item.dialplan_number}</NtText>
      </NtModalSubtitle>

      <ScrollView
        scrollEnabled={true}
        contentContainerStyle={{ flexGrow: 1 }}
        showsVerticalScrollIndicator={false}
      >
        <NtModalInputField
          containerStyle={{ marginTop: 15 }}
          title={"Name *"}
          placeholder={"Name"}
          value={name}
          setValue={(text) => setName(text)}
        />
        <NtModalInputField
          containerStyle={{ marginTop: 15 }}
          title={"Destination *"}
          placeholder={"Destination"}
          value={destination}
          setValue={(text) => setDestination(text.replace(/\D/g, ""))}
          keyboardType="numeric"
        />

        <NtModalInputField
          containerStyle={{ marginTop: 15 }}
          title="Description"
          placeholder="Optional — defaults to auto-generated summary"
          value={description}
          setValue={setDescription}
        />

        <NtModalAccountPickerField
          containerStyle={{ marginTop: 15 }}
          title={"Route Call to"}
          placeholder={"Route Call to"}
          value={action}
          setValue={(text) => setAction(text)}
        />

        <View
          style={{ flexDirection: "row", alignItems: "flex-start", marginTop: 15 }}
        >
          <NtModalInputField
            containerStyle={{ flex: 1 }}
            title="Caller ID Name Override"
            placeholder="Optional"
            value={callerIdName}
            setValue={setCallerIdName}
          />
          <NtModalInputField
            containerStyle={{ flex: 1, marginLeft: 10 }}
            title="Caller ID Number Override"
            placeholder="Optional digits"
            value={callerIdNumber}
            setValue={(text) => setCallerIdNumber(text.replace(/\D/g, ""))}
            keyboardType="numeric"
          />
        </View>

        <NtModalInputField
          containerStyle={{ marginTop: 15 }}
          title="Order"
          placeholder="100"
          value={order}
          setValue={(text) => setOrder(text.replace(/\D/g, ""))}
          keyboardType="numeric"
          footer="Rule priority when multiple inbound routes match — lower runs first."
        />

        <View style={{ marginTop: 16 }}>
          <NtToggleRow
            value={enabled}
            onChange={setEnabled}
            label="Enable inbound route"
            sub="When off, calls to this destination won't follow this route."
          />
        </View>

        <NtModalError error={error} containerStyle={{ marginTop: 6 }} />
      </ScrollView>

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 40,
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <NtModalActiveButton
            containerStyle={{ backgroundColor: "red" }}
            title={"Delete"}
            icon={"delete"}
            onPress={() => {
              handleDelete();
            }}
            enabled={true}
          />

          <NtModalActiveButton
            containerStyle={{ marginLeft: 10 }}
            title={"Edit XML"}
            icon={"xml"}
            onPress={() => xmlModalRef.current?.show()}
            enabled={true}
          />
        </View>

        <View style={{ flexDirection: "row", alignItems: "center" }}>
          {loading && (
            <NtActivityIndicator
              color={colors.blue}
              size="small"
              containerStyle={{ marginRight: 25 }}
            />
          )}

          <NtModalUnactiveButton title={"Cancel"} onPress={onCancel} />

          <NtModalActiveButton
            containerStyle={{ marginLeft: 15 }}
            title={"Update"}
            onPress={() => {
              if (isRequiredFields()) {
                handleUpdate();
              }
            }}
            enabled={isRequiredFields()}
          />
        </View>
      </View>

      <NtModal ref={xmlModalRef}>
        <NtInboundRouteXmlEditor
          item={item}
          onCancel={() => xmlModalRef.current?.dismiss()}
          onSave={() => {
            xmlModalRef.current?.dismiss();
            onUpdate?.();
          }}
        />
      </NtModal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minWidth: 500,
    maxWidth: 600,
    padding: 15,
  },
  fieldTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: colors.darker,
  },
});

export default NtInboundRouteUpdateForm;
